import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, Clock, FileText, Ticket as TicketIcon } from "lucide-react";
import { api } from "@/lib/api";
import { formatDateTime } from "@/lib/utils";
import { Badge, Button } from "@/components/ui/primitives";
import { useAuth } from "@/features/auth/auth-context";
import { listTickets } from "@/features/tickets/api";
import { STATUS_LABELS, PRIORITY_LABELS, priorityStyle, statusTone } from "@/features/tickets/labels";
import { slaBadge } from "@/features/tickets/sla";
import { TicketDetailDialog } from "@/features/tickets/TicketDetailDialog";
import { NewTicketButton } from "./NewTicketButton";

const ACTIVE_STATUSES = ["open", "assigned", "in_progress", "waiting_customer"] as const;

interface Counts {
  total: number;
  by_status: Record<string, number>;
}

/**
 * The customer's home screen.
 *
 * Everything here is already scoped server-side to the customer's own
 * organisation (M4 row-level scoping), so this is a focused presentation over
 * endpoints the customer role already reaches — not a new trust boundary.
 */
export default function PortalDashboard() {
  const { user } = useAuth();
  const [detailId, setDetailId] = useState<string | null>(null);

  const { data: counts } = useQuery({
    queryKey: ["tickets", "counts"],
    queryFn: () => api.get<Counts>("/tickets/counts"),
  });

  // The most recent handful, plus a dedicated "awaiting your approval" list —
  // the action only the customer can take.
  const { data: recent } = useQuery({
    queryKey: ["tickets", { portal: "recent" }],
    queryFn: () => listTickets({ page: 1, page_size: 5 }),
  });
  const { data: awaiting } = useQuery({
    queryKey: ["tickets", { portal: "awaiting" }],
    queryFn: () => listTickets({ page: 1, page_size: 20, status: ["resolved"] }),
  });
  // Active requests, so we can surface any that have breached their resolution
  // SLA (overdue) — the customer's own tickets, already scoped server-side.
  const { data: active } = useQuery({
    queryKey: ["tickets", { portal: "active" }],
    queryFn: () => listTickets({ page: 1, page_size: 100, status: [...ACTIVE_STATUSES] }),
  });

  const byStatus = counts?.by_status ?? {};
  const open =
    (byStatus.open ?? 0) +
    (byStatus.assigned ?? 0) +
    (byStatus.in_progress ?? 0) +
    (byStatus.waiting_customer ?? 0);
  const awaitingCount = awaiting?.total ?? 0;
  const overdue = (active?.items ?? []).filter(
    (t) => t.sla.resolution_state === "breached",
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            Welcome, {user?.full_name}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Your support requests at a glance.
          </p>
        </div>
        <NewTicketButton />
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={TicketIcon}
          label="Open requests"
          value={open}
          onClick={() => setDetailId(null)}
        />
        <StatCard
          icon={AlertTriangle}
          label="Overdue (SLA breached)"
          value={overdue.length}
          tone={overdue.length > 0 ? "danger" : "muted"}
        />
        <StatCard
          icon={Clock}
          label="Awaiting your approval"
          value={awaitingCount}
          tone={awaitingCount > 0 ? "warning" : "muted"}
        />
        <StatCard
          icon={CheckCircle2}
          label="Closed"
          value={byStatus.closed ?? 0}
          tone="success"
        />
      </div>

      {overdue.length > 0 && (
        <section>
          <h2 className="mb-2 flex items-center gap-2 text-sm font-medium">
            <AlertTriangle className="h-4 w-4 text-destructive" aria-hidden="true" />
            Overdue — past their resolution deadline
          </h2>
          <ul className="space-y-2">
            {overdue.map((t) => {
              const sla = slaBadge(t.sla.resolution_state, t.sla.resolution_due_at);
              return (
                <li
                  key={t.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-destructive/40 bg-destructive/5 p-3"
                >
                  <div>
                    <button
                      type="button"
                      onClick={() => setDetailId(t.id)}
                      className="text-left font-medium hover:underline"
                    >
                      {t.title}
                    </button>
                    <div className="text-xs text-muted-foreground">
                      {t.ticket_number} · {STATUS_LABELS[t.status]}
                    </div>
                  </div>
                  <Badge tone={sla.tone}>{sla.label}</Badge>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {awaitingCount > 0 && (
        <section>
          <h2 className="mb-2 text-sm font-medium">
            Resolved — please confirm or reopen
          </h2>
          <ul className="space-y-2">
            {awaiting?.items.map((t) => (
              <li
                key={t.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-sla-warn/40 bg-sla-warn/5 p-3"
              >
                <div>
                  <button
                    type="button"
                    onClick={() => setDetailId(t.id)}
                    className="text-left font-medium hover:underline"
                  >
                    {t.title}
                  </button>
                  <div className="text-xs text-muted-foreground">
                    {t.ticket_number} · resolved {formatDateTime(t.updated_at)}
                  </div>
                </div>
                <Button variant="outline" onClick={() => setDetailId(t.id)}>
                  <FileText className="h-4 w-4" aria-hidden="true" />
                  Review
                </Button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section>
        <h2 className="mb-2 text-sm font-medium">Recent requests</h2>
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full min-w-[600px] text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-4 py-2.5 font-medium">Request</th>
                <th className="px-4 py-2.5 font-medium">Priority</th>
                <th className="px-4 py-2.5 font-medium">Status</th>
                <th className="px-4 py-2.5 font-medium">Resolution SLA</th>
                <th className="px-4 py-2.5 font-medium">Raised</th>
              </tr>
            </thead>
            <tbody>
              {recent?.items.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">
                    You haven't raised any requests yet.
                  </td>
                </tr>
              )}
              {recent?.items.map((t) => (
                <tr key={t.id} className="border-t border-border">
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => setDetailId(t.id)}
                      className="text-left font-medium hover:underline"
                    >
                      {t.title}
                    </button>
                    <div className="font-mono text-xs text-muted-foreground">
                      {t.ticket_number}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className="rounded-full px-2 py-0.5 text-xs font-medium text-white"
                      style={priorityStyle(t.priority)}
                    >
                      {PRIORITY_LABELS[t.priority]}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <Badge tone={statusTone(t.status)}>{STATUS_LABELS[t.status]}</Badge>
                  </td>
                  <td className="px-4 py-3">
                    {(() => {
                      const sla = slaBadge(t.sla.resolution_state, t.sla.resolution_due_at);
                      return <Badge tone={sla.tone}>{sla.label}</Badge>;
                    })()}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {formatDateTime(t.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <TicketDetailDialog
        ticketId={detailId}
        onClose={() => setDetailId(null)}
        engineers={[]}
      />
    </div>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  tone = "muted",
  onClick,
}: {
  icon: typeof TicketIcon;
  label: string;
  value: number;
  tone?: "muted" | "warning" | "success" | "danger";
  onClick?: () => void;
}) {
  const accent =
    tone === "danger"
      ? "text-destructive"
      : tone === "warning"
        ? "text-sla-warn"
        : tone === "success"
          ? "text-sla-ok"
          : "text-primary";
  return (
    <div className="rounded-lg border border-border bg-card p-5 text-card-foreground" onClick={onClick}>
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Icon className={`h-4 w-4 ${accent}`} aria-hidden="true" />
        {label}
      </div>
      <div className="mt-2 text-3xl font-semibold tabular-nums">{value}</div>
    </div>
  );
}

